import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';

const LessonForm = () => {
    const { courseId, lessonId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [sections, setSections] = useState([]);
    const [videoFile, setVideoFile] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        content_type: 'text',
        content: { body: '', url: '' },
        order: 1,
        duration_minutes: 15,
        section: '',
    });

    useEffect(() => {
        fetchSections();
        if (lessonId) {
            setIsEdit(true);
            fetchLesson();
        }
    }, [lessonId, courseId]);

    const fetchSections = async () => {
        try {
            const response = await api.get(`/courses/${courseId}/`);
            setSections(response.data.sections || []);
            if (response.data.sections?.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    section: response.data.sections[0].id
                }));
            }
        } catch (err) {
            console.error('Błąd pobierania sekcji:', err);
        }
    };

    const fetchLesson = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/lessons/${lessonId}/`);
            const lesson = response.data;
            setFormData({
                title: lesson.title || '',
                content_type: lesson.content_type || 'text',
                content: lesson.content || { body: '', url: '' },
                order: lesson.order || 1,
                duration_minutes: lesson.duration_minutes || 15,
                section: lesson.section || '',
            });
        } catch (err) {
            setError('Nie udało się pobrać lekcji');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'content_url') {
            setFormData({
                ...formData,
                content: { ...formData.content, url: value },
            });
        } else if (name === 'content_body') {
            setFormData({
                ...formData,
                content: { ...formData.content, body: value },
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    const handleContentChange = (value) => {
        setFormData({
            ...formData,
            content: { ...formData.content, body: value },
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setVideoFile(file);
            const url = URL.createObjectURL(file);
            setFormData({
                ...formData,
                content: { ...formData.content, url: url },
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const sectionToUse = formData.section;

            if (!sectionToUse) {
                setError('Wybierz sekcję przed dodaniem lekcji');
                setSaving(false);
                return;
            }

            if (videoFile) {
                const uploadData = new FormData();
                uploadData.append('title', formData.title);
                uploadData.append('content_type', 'video');
                uploadData.append('section', sectionToUse);
                uploadData.append('order', 1);
                uploadData.append('duration_minutes', formData.duration_minutes || 15);
                uploadData.append('video_file', videoFile);

                if (isEdit) {
                    await api.patch(`/lessons/${lessonId}/`, uploadData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                } else {
                    await api.post(`/sections/${sectionToUse}/upload_lesson/`, uploadData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                }
                setSuccess('✅ Lekcja utworzona!');
                setTimeout(() => navigate(`/courses/${courseId}`), 1500);
                setSaving(false);
                return;
            }

            const data = { ...formData };
            data.section = sectionToUse;


            if (formData.content_type === 'video') {
                data.content = { url: formData.content?.url || '' };
            }

            if (formData.content_type === 'text') {
                data.content = { body: formData.content?.body || '' };
            }

            if (formData.content_type === 'quiz' || formData.content_type === 'assignment') {
                data.content = {};
            }

            if (isEdit) {
                await api.patch(`/lessons/${lessonId}/`, data);
            } else {
                await api.post(`/sections/${sectionToUse}/add_lesson/`, data);
            }
            setSuccess('✅ Lekcja utworzona!');
            setTimeout(() => navigate(`/courses/${courseId}`), 1500);

        } catch (err) {
            console.error('❌ Błąd zapisu:', err);
            if (err.response?.data) {
                const errors = err.response.data;
                const firstError = Object.values(errors).flat()[0];
                setError(firstError || 'Błąd zapisu lekcji');
            } else if (err.code === 'ERR_NETWORK') {
                setError('Błąd połączenia z serwerem. Sprawdź czy backend jest uruchomiony.');
            } else {
                setError('Błąd połączenia z serwerem');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Ładowanie...</p>
            </Container>
        );
    }

    return (
        <Container className="mt-4 mb-5">
            <Row>
                <Col md={10} className="mx-auto">
                    <Card className="shadow-sm">
                        <Card.Body>
                            <h2 className="mb-4">
                                {isEdit ? '✏️ Edytuj lekcję' : '📝 Dodaj nową lekcję'}
                            </h2>

                            {error && <Alert variant="danger">{error}</Alert>}
                            {success && <Alert variant="success">{success}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tytuł lekcji *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        placeholder="Wprowadź tytuł lekcji"
                                        required
                                    />
                                </Form.Group>

                                {/* WYBÓR SEKCJI - ZAWSZE WIDOCZNY */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Sekcja *</Form.Label>
                                    <Form.Select
                                        name="section"
                                        value={formData.section}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Wybierz sekcję...</option>
                                        {sections.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.title}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Typ lekcji</Form.Label>
                                            <Form.Select
                                                name="content_type"
                                                value={formData.content_type}
                                                onChange={handleChange}
                                            >
                                                <option value="text">📝 Tekst</option>
                                                <option value="video">📹 Wideo</option>
                                                <option value="quiz">📊 Quiz</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Czas trwania (minuty)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="duration_minutes"
                                                value={formData.duration_minutes}
                                                onChange={handleChange}
                                                min="1"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Reszta formularza... */}
                                {formData.content_type === 'text' && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Treść lekcji</Form.Label>
                                        <ReactQuill
                                            theme="snow"
                                            value={formData.content?.body || ''}
                                            onChange={handleContentChange}
                                            placeholder="Wprowadź treść lekcji..."
                                            style={{ height: '200px', marginBottom: '50px' }}
                                        />
                                    </Form.Group>
                                )}

                                {formData.content_type === 'video' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>📹 Plik wideo</Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept="video/*"
                                                onChange={handleFileChange}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>🔗 Link do wideo</Form.Label>
                                            <Form.Control
                                                type="url"
                                                name="content_url"
                                                value={formData.content?.url || ''}
                                                onChange={handleChange}
                                                placeholder="https://www.youtube.com/watch?v=..."
                                            />
                                        </Form.Group>

                                        {formData.content?.url && (
                                            <div className="mb-3">
                                                <Form.Label>Podgląd</Form.Label>
                                                <div className="ratio ratio-16x9">
                                                    <video controls src={formData.content.url} style={{ maxWidth: '100%' }}>
                                                        Twoja przeglądarka nie obsługuje wideo.
                                                    </video>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="d-flex gap-2 mt-4">
                                    <Button variant="primary" type="submit" disabled={saving}>
                                        {saving ? 'Zapisywanie...' : isEdit ? '💾 Zapisz' : '➕ Dodaj lekcję'}
                                    </Button>
                                    <Button variant="secondary" onClick={() => navigate(`/courses/${courseId}`)}>
                                        Anuluj
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default LessonForm;